/**
 * Time helpers and time-window constants for the Cost Scheme board.
 *
 * The board draws a linear day timeline (the "osa"). Timed parts carry a fractional start hour and a
 * duration in hours; the server exchanges them as `HH:MM:SS` (TimeOnly) strings, so this module owns the
 * conversions between the two representations plus the visible day-window bounds. Pixel geometry (how an
 * hour maps to on-screen height, lane widths, …) lives in `layout.ts`.
 */

/** Default start hour of a newly created timed part / the collapsed day window (06:00). */
export const DEFAULT_START = 6;
/** Start of the expanded ("night") window (00:00). */
export const NIGHT_START = 0;
/** End of the day window (24:00). */
export const DAY_END = 24;
/** Default duration (hours) for a freshly dropped timed part. */
export const DEFAULT_TIMED_HOURS = 2;

/** Snap a fractional hour to the nearest 15 minutes. */
export const snapQuarter = (h: number): number => Math.round(h * 4) / 4;

/** Fractional hour → `HH:MM`. */
export function fmtH(h: number): string {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/** `HH:MM:SS` (TimeOnly) → fractional hours, or `null`. */
export function timeToHours(time: string | null | undefined): number | null {
  if (!time) return null;
  const [h, m] = time.split(':').map(Number);
  if (Number.isNaN(h)) return null;
  return h + (Number.isNaN(m) ? 0 : m / 60);
}

/** Fractional hours → `HH:MM:SS` (TimeOnly). */
export function hoursToTime(h: number): string {
  const clamped = Math.max(0, Math.min(24, h));
  const hh = Math.floor(clamped);
  const mm = Math.round((clamped - hh) * 60);
  // 60 minutes rounds into the next hour.
  const normHh = mm === 60 ? hh + 1 : hh;
  const normMm = mm === 60 ? 0 : mm;
  return `${String(normHh).padStart(2, '0')}:${String(normMm).padStart(2, '0')}:00`;
}
